import * as THREE from 'three';
import { SketchContour } from '../../mesh/meshProxy/SketchContour';
import {
    AbstractSketchEdgeObject,
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchParabolaObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    type SketchSlotObject,
} from '../../mesh/sketch';
import { SketchCollectionCreationSource, type SketchCollectionObject } from '../../mesh/sketch/SketchCollectionObject';
import type { SketchPointObject } from '../../mesh/sketch/SketchPointObject';
import type { Viewer } from '../../viewer/Viewer';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { AbstractEntityDragStrategy, EntityDragStrategyType } from './AbstractEntityDragStrategy';
import { SketchRectangleConstructionLineResolver } from '../../sketch/rectangle';
import type {SketchParametricDragSession} from '../../sketch/solver/runtime/SketchParametricDragSession';

/**
 * 轮廓点位快照。
 */
interface ContourPointSnapshot {
    /** 点对象。 */
    point: SketchPointObject;
    /** 拖拽开始时世界坐标。 */
    startWorldPosition: THREE.Vector3;
}

/**
 * 轮廓端点访问协议。
 */
interface ContourSourceEndpointAccessor {
    /** 获取边起点。 */
    getEdgeStartPoint: () => SketchPointObject;
    /** 获取边终点。 */
    getEdgeEndPoint: () => SketchPointObject;
}

/**
 * 判断对象是否为轮廓拖拽策略。
 * @param value 待判断对象。
 * @returns 是否为轮廓拖拽策略。
 */
export function isContourDragStrategy(value: AbstractEntityDragStrategy): value is ContourDragStrategy {
    return value.strategyType === EntityDragStrategyType.Contour;
}

/**
 * 轮廓拖拽策略。
 *
 * 说明：
 * 1. 该策略按拖拽起点快照生成绝对目标，避免逐帧增量累积导致“跟手漂移”；
 * 2. 圆、椭圆、圆弧、样条、集合与槽口轮廓都把全部定义点编译为同一多点公式意图；
 * 3. 本类不直接写点、定义或派生 helper，求解与原子写回统一交给 Viewer 参数协调入口。
 */
export class ContourDragStrategy extends AbstractEntityDragStrategy {
    /** 策略类型。 */
    public readonly strategyType = EntityDragStrategyType.Contour;

    /** 当前轮廓对象。 */
    private readonly contour: SketchContour;

    /** 矩形构造线归属解析服务。 */
    private readonly rectangleConstructionLineResolver = new SketchRectangleConstructionLineResolver();

    /** 拖拽参考平面。 */
    private dragPlane: THREE.Plane | null = null;

    /** 拖拽起始命中点。 */
    private dragStartWorldPoint: THREE.Vector3 | null = null;

    /** 拖拽起始点快照。 */
    private sourcePointSnapshots: ContourPointSnapshot[] = [];

    /** 轮廓最后一次请求的绝对平移矩阵，供 mouseup 终局求解。 */
    private readonly requestedParametricTranslation = new THREE.Matrix4();

    /** 是否存在尚需在 mouseup 确认的参数化轮廓平移。 */
    private hasRequestedParametricTranslation = false;

    /** pointerdown 时捕获基线的 FreeCAD 式参数拖拽会话。 */
    private parametricDragSession: SketchParametricDragSession | null = null;

    /**
     * 构造函数。
     * @param renderer Viewer 实例。
     * @param contour 当前轮廓对象。
     */
    public constructor(renderer: Viewer, contour: SketchContour) {
        super(renderer);
        this.contour = contour;
    }

    /**
     * 获取当前轮廓对象。
     * @returns 轮廓对象。
     */
    public getContour(): SketchContour {
        return this.contour;
    }

    /**
     * 开始轮廓拖拽。
     * @param payload 拖拽启动事件载荷。
     */
    public start(payload: RawPointerEventPayload): void {
        this.resetAdaptiveParametricPreviewState();
        this.hasRequestedParametricTranslation = false;
        this.parametricDragSession?.dispose();
        this.parametricDragSession = null;
        this.dragPreviewOwner = this.contour.source;
        // 轮廓来源是实体级固定关系的权威宿主；不可拖动时不得创建预览或历史事务。
        if (!this.contour.source.canDrag()) {
            this.dragPreviewOwner = null;
            return;
        }
        const centroid = this.computeCentroid();
        this.dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
            this.contour.source.referencePlane.normal.clone(),
            centroid,
        );

        this.dragStartWorldPoint = this.resolvePointerIntersectionOnDragPlane(payload);
        if (!this.dragStartWorldPoint) {
            this.dragPreviewOwner = null;
            return;
        }
        if (!this.captureSourceSnapshots()) {
            this.dragPreviewOwner = null;
            this.dragStartWorldPoint = null;
            this.dragPlane = null;
            return;
        }
        this.beginSketchRuntimeHistorySession('移动草图轮廓', 'sketch.runtime.drag.contour');
        /**
         * 拖拽基线必须由本次实际目标点建立：
         * 来源根承载 FixSlot、实体级对称等关系，定义点承载本次直接目标；两者必须同时作为种子。
         * 只传来源根会让普通集合形成零点 island，只传点又会漏掉槽口本体上的实体级关系。
         */
        const parametricDragSeedPoints = this.sourcePointSnapshots.map((snapshot) => snapshot.point);
        this.parametricDragSession = this.renderer.beginActiveSketchParametricDragSession(
            [this.contour.source, ...parametricDragSeedPoints],
        );
        if (!this.parametricDragSession) {
            this.cancelSketchRuntimeHistorySession();
            this.dragPreviewOwner = null;
            this.dragStartWorldPoint = null;
            this.dragPlane = null;
            this.sourcePointSnapshots = [];
            return;
        }
        try {
            this.invokeDragPreviewBegin(this.contour.source);
        } catch (error) {
            this.cancelSketchRuntimeHistorySession();
            this.parametricDragSession?.dispose();
            this.parametricDragSession = null;
            this.dragPreviewOwner = null;
            console.error('轮廓拖拽留影启动失败。', { error, source: this.contour.source });
            return;
        }

        // 草图轮廓拖拽期间禁用相机控制，避免左键拖拽触发视图平移。
        this.disableCameraControlsForDrag();
    }

    /**
     * 更新轮廓拖拽位置。
     * @param payload 鼠标事件载荷。
     */
    public update(payload: RawPointerEventPayload): void {
        if (!this.dragPlane || !this.dragStartWorldPoint) {
            return;
        }

        if ((payload.buttons & 1) !== 1) {
            return;
        }

        const camera = this.renderer.camera;
        if (!camera) {
            return;
        }

        if (!this.updateMouseNdc(payload.clientX, payload.clientY)) {
            return;
        }

        this.raycaster.setFromCamera(this.mouseNdc, camera);
        const intersection = new THREE.Vector3();
        if (!this.raycaster.ray.intersectPlane(this.dragPlane, intersection)) {
            return;
        }

        const translationMatrix = this.buildTranslationMatrix(this.dragStartWorldPoint, intersection);
        this.requestedParametricTranslation.copy(translationMatrix);
        this.hasRequestedParametricTranslation = true;
        const applied = this.runAdaptiveParametricPreview(() => (
            this.applyParametricTranslation(this.requestedParametricTranslation)
        ));

        if (applied === true) {
            this.markGeometryChanged();
        }
        payload.nativeEvent.preventDefault();
    }

    /**
     * 停止轮廓拖拽。
     */
    protected doStop(): void {
        try {
            // 外部事务已替换草图时，旧基线不再具备写回 authority，必须直接放弃整个手势。
            if (this.discardStaleSketchDrag(this.parametricDragSession)) {
                return;
            }
            /**
             * 仅补算被节流的最终矩阵；普通失败帧可在提交历史前重新验证同一目标，
             * stale 帧已在上方永久拒绝。
             */
            let applied = this.flushPendingParametricPreview();
            if (applied === null
                && this.hasRequestedParametricTranslation
                && !this.parametricDragSession?.canReuseLastAppliedFrame()) {
                applied = this.applyParametricTranslation(this.requestedParametricTranslation);
            }
            if (applied === true) {
                this.markGeometryChanged();
            }
            // pending 补帧也可能观察到外部版本变化，提交历史前必须再次确认 authority。
            if (this.discardStaleSketchDrag(this.parametricDragSession)) {
                return;
            }
            this.commitSketchRuntimeHistorySession();
        } finally {
            this.parametricDragSession?.dispose();
            this.parametricDragSession = null;
            this.dragPlane = null;
            this.dragStartWorldPoint = null;
            this.sourcePointSnapshots = [];
            this.hasRequestedParametricTranslation = false;
            this.resetAdaptiveParametricPreviewState();
        }
    }

    /**
     * 计算轮廓质心。
     * @returns 轮廓质心世界坐标。
     */
    private computeCentroid(): THREE.Vector3 {
        const points = this.contour.getContourPoints();
        const centroid = new THREE.Vector3();
        points.forEach((point) => centroid.add(point));
        if (points.length > 0) {
            centroid.divideScalar(points.length);
        }
        return centroid;
    }

    /**
     * 计算拖拽起始命中点。
     * @param payload 拖拽启动事件载荷。
     * @returns 世界坐标命中点；无法解析时返回 null，并由 start 阶段中止拖拽。
     */
    private resolvePointerIntersectionOnDragPlane(payload: RawPointerEventPayload): THREE.Vector3 | null {
        if (!this.dragPlane) {
            console.error('轮廓拖拽参考平面未初始化。', { source: this.contour.source });
            return null;
        }

        const camera = this.renderer.camera;
        if (!camera) {
            console.error('当前相机不可用，无法开始轮廓拖拽。', { source: this.contour.source });
            return null;
        }

        if (!this.updateMouseNdc(payload.clientX, payload.clientY)) {
            console.error('渲染容器不可用，无法开始轮廓拖拽。', { source: this.contour.source });
            return null;
        }

        this.raycaster.setFromCamera(this.mouseNdc, camera);
        const intersection = new THREE.Vector3();
        if (!this.raycaster.ray.intersectPlane(this.dragPlane, intersection)) {
            console.error('鼠标射线未命中轮廓拖拽平面。', { source: this.contour.source });
            return null;
        }

        return intersection;
    }

    /**
     * 构造平移矩阵。
     * @param start 起始命中点。
     * @param current 当前命中点。
     * @returns 平移矩阵。
     */
    private buildTranslationMatrix(start: THREE.Vector3, current: THREE.Vector3): THREE.Matrix4 {
        const delta = current.clone().sub(start);
        return new THREE.Matrix4().makeTranslation(delta.x, delta.y, delta.z);
    }

    /**
     * 捕获来源对象快照。
     * @returns 捕获成功返回 true；来源不支持或快照为空时记录错误并返回 false。
     */
    private captureSourceSnapshots(): boolean {
        const source = this.contour.source;

        const pointSnapshotMap = new Map<string, ContourPointSnapshot>();

        if (isSketchCircleObject(source)) {
            this.collectPointSnapshot(source.getCenterControlPoint(), pointSnapshotMap);
            this.collectPointSnapshot(source.getEdgeControlPoint(), pointSnapshotMap);
        } else if (isSketchEllipseObject(source)) {
            this.captureEllipseSourceSnapshots(source, pointSnapshotMap);
        } else if (isSketchArcObject(source)) {
            this.captureArcSourceSnapshots(source, pointSnapshotMap);
        } else if (isSketchCollectionObject(source)) {
            this.captureCollectionSourceSnapshots(Array.from(source.collectSet), pointSnapshotMap);
            // 矩形构造线挂在草图组下而不是 collectSet 中，轮廓拖动必须按矩形归属记录一并纳入快照。
            this.captureRectangleConstructionSourceSnapshots(source, pointSnapshotMap);
            
            // 多边形集合挂载的辅助圆不在 collectSet 中，需单独捕获两个定义点。
            if (source.creationSource === SketchCollectionCreationSource.Polygon) {
                this.capturePolygonConstructionCircleSnapshots(source, pointSnapshotMap);
            }
            
        } else if (isSketchSlotObject(source)) {
            this.captureSlotSourceSnapshots(source, pointSnapshotMap);
        } else if (isSketchSplineObject(source) || isSketchStyleSplineObject(source)) {
            // 两类样条作为自闭合轮廓来源时，需要捕获全部控制点参与整体平移。
            source.getControlPoints().forEach((controlPoint) => {
                this.collectPointSnapshot(controlPoint, pointSnapshotMap);
            });
        } else {
            console.error('未支持的轮廓来源类型，无法创建拖拽快照。', { source });
            return false;
        }

        this.sourcePointSnapshots = Array.from(pointSnapshotMap.values());
        if (this.sourcePointSnapshots.length < 1) {
            console.error('轮廓拖拽快照为空。', { source });
            return false;
        }
        return true;
    }

    /**
     * 捕获椭圆来源快照。
     * @param source 椭圆对象。
     * @param snapshotMap 点快照映射。
     */
    private captureEllipseSourceSnapshots(
        source: {
            getCenterPoint: () => SketchPointObject;
            getCardinalControlPointsSnapshot: () => ReadonlyMap<unknown, SketchPointObject>;
        } & AbstractSketchEdgeObject,
        snapshotMap: Map<string, ContourPointSnapshot>,
    ): void {
        const endpoints = this.getCollectionSourceEndpoints(source);
        if (!endpoints) {
            return;
        }
        const { startPoint, endPoint } = endpoints;
        this.collectPointSnapshot(startPoint, snapshotMap);
        this.collectPointSnapshot(endPoint, snapshotMap);
        this.collectPointSnapshot(source.getCenterPoint(), snapshotMap);
        source.getCardinalControlPointsSnapshot().forEach((point) => {
            this.collectPointSnapshot(point, snapshotMap);
        });
    }

    /**
     * 把轮廓的绝对平移目标编译为同一条多点操作意图。
     *
     * 所有目标都从 pointerdown 快照计算，禁止逐帧累加；调用前不改运行时对象，
     * 求解失败时也不回退对象式传播。
     *
     * @param translationMatrix 从拖拽起点到当前目标的绝对平移矩阵。
     * @returns 是否完成参数解写回。
     */
    private applyParametricTranslation(translationMatrix: THREE.Matrix4): boolean {
        const pointTargets = new Map<SketchPointObject, THREE.Vector3>();

        this.sourcePointSnapshots.forEach((snapshot) => {
            pointTargets.set(
                snapshot.point,
                snapshot.startWorldPosition.clone().applyMatrix4(translationMatrix),
            );
        });

        return this.renderer.previewSketchParametricPointTargets(
            pointTargets,
            `contour-translate:${this.contour.source.uuid}`,
            this.parametricDragSession ?? undefined,
        );
    }

    /**
     * 捕获圆弧来源快照。
     * @param source 圆弧对象。
     * @param snapshotMap 点快照映射。
     */
    private captureArcSourceSnapshots(
        source: AbstractSketchEdgeObject,
        snapshotMap: Map<string, ContourPointSnapshot>,
    ): void {
        if (!isSketchArcObject(source)) {
            console.error('轮廓来源不是圆弧，无法创建圆弧拖拽快照。', { source });
            return;
        }
        const endpoints = this.getCollectionSourceEndpoints(source);
        if (!endpoints) {
            return;
        }
        const { startPoint, endPoint } = endpoints;
        this.collectPointSnapshot(source.getCenterPoint(), snapshotMap);
        this.collectPointSnapshot(startPoint, snapshotMap);
        this.collectPointSnapshot(endPoint, snapshotMap);
    }

    /**
     * 捕获集合来源快照。
     * @param sources 集合内边对象列表。
     * @param snapshotMap 点快照映射。
     */
    private captureCollectionSourceSnapshots(
        sources: readonly AbstractSketchEdgeObject[],
        snapshotMap: Map<string, ContourPointSnapshot>,
    ): void {
        sources.forEach((source) => {
            this.captureCollectionEdgeSourceSnapshots(source, snapshotMap);
        });
    }

    /**
     * 捕获单条集合边的快照。
     * @param source 边对象。
     * @param snapshotMap 点快照映射。
     */
    private captureCollectionEdgeSourceSnapshots(
        source: AbstractSketchEdgeObject,
        snapshotMap: Map<string, ContourPointSnapshot>,
    ): void {
        if (isSketchSplineObject(source) || isSketchStyleSplineObject(source)) {
            source.getControlPoints().forEach((controlPoint) => {
                this.collectPointSnapshot(controlPoint, snapshotMap);
            });
            return;
        }

        if (isSketchParabolaObject(source)) {
            source.getControlPoints().forEach((controlPoint) => {
                this.collectPointSnapshot(controlPoint, snapshotMap);
            });
            return;
        }

        if (isSketchArcObject(source)) {
            this.collectPointSnapshot(source.getCenterPoint(), snapshotMap);
        }

        const endpoints = this.getCollectionSourceEndpoints(source);
        if (!endpoints) {
            return;
        }
        const { startPoint, endPoint } = endpoints;
        this.collectPointSnapshot(startPoint, snapshotMap);
        this.collectPointSnapshot(endPoint, snapshotMap);
    }

    
    /**
     * 捕获矩形附加构造线快照。
     *
     * 设计说明：
     * 1. 矩形集合的轮廓来源只包含外框边，附加构造线通过 `geometryData.constructionLines` 归属到矩形；
     * 2. 从角点构造线复用外框角点，必须随轮廓平移；从中点构造线只有存在自动中点关系时才随轮廓平移；
     * 3. 使用轮廓拖拽专用解析结果，避免未开启自动几何关系的中点构造线被误当成外框附属几何。
     *
     * @param collection 矩形集合对象。
     * @param snapshotMap 点快照映射。
     */
    private captureRectangleConstructionSourceSnapshots(
        collection: SketchCollectionObject,
        snapshotMap: Map<string, ContourPointSnapshot>,
    ): void {
        const constructionGeometry = this.rectangleConstructionLineResolver.resolveContourDragConstructionGeometry(collection);
        constructionGeometry.lines.forEach((line) => {
            this.captureCollectionEdgeSourceSnapshots(line, snapshotMap);
        });
        constructionGeometry.points.forEach((point) => {
            this.collectPointSnapshot(point, snapshotMap);
        });
    }
    

    /**
     * 捕获槽口来源快照。
     * @param slot 槽口对象。
     * @param snapshotMap 点快照映射。
     */
    private captureSlotSourceSnapshots(
        slot: SketchSlotObject,
        snapshotMap: Map<string, ContourPointSnapshot>,
    ): void {
        slot.getJunctionPointArray().forEach((point) => {
            this.collectPointSnapshot(point, snapshotMap);
        });

        slot.getArcs().forEach((arc) => {
            this.collectPointSnapshot(arc.getCenterPoint(), snapshotMap);
        });

        if (slot.centerlineStartPoint) {
            this.collectPointSnapshot(slot.centerlineStartPoint, snapshotMap);
        }
        if (slot.centerlineEndPoint) {
            this.collectPointSnapshot(slot.centerlineEndPoint, snapshotMap);
        }
        if (slot.centerPoint) {
            this.collectPointSnapshot(slot.centerPoint, snapshotMap);
        }
        if (slot.arcCenterPoint) {
            this.collectPointSnapshot(slot.arcCenterPoint, snapshotMap);
        }
        if (slot.centerArcMidpoint) {
            this.collectPointSnapshot(slot.centerArcMidpoint, snapshotMap);
        }
    }

    /**
     * 捕获多边形集合下全部辅助构造圆定义点。
     *
     * 辅助圆不在集合 `collectSet` 中，必须把圆心与圆周控制点都加入同一多点意图；
     * 只移动圆心会让运行时圆刷新按旧圆周点反算出错误半径。
     *
     * @param collection 多边形集合对象。
     * @param snapshotMap 本次轮廓拖拽的定义点快照映射。
     */
    private capturePolygonConstructionCircleSnapshots(
        collection: SketchCollectionObject,
        snapshotMap: Map<string, ContourPointSnapshot>,
    ): void {
        collection.children.forEach((child) => {
            if (!isSketchCircleObject(child) || !child.isConstruction) {
                return;
            }
            this.collectPointSnapshot(child.getCenterControlPoint(), snapshotMap);
            this.collectPointSnapshot(child.getEdgeControlPoint(), snapshotMap);
        });
    }

    /**
     * 记录点位快照。
     * @param point 点对象。
     * @param snapshotMap 点快照映射。
     */
    private collectPointSnapshot(
        point: SketchPointObject,
        snapshotMap: Map<string, ContourPointSnapshot>,
    ): void {
        if (snapshotMap.has(point.uuid)) {
            return;
        }
        snapshotMap.set(point.uuid, {
            point,
            startWorldPosition: point.getPosition(),
        });
    }

    /**
     * 获取集合源对象端点。
     * @param source 集合边对象。
     * @returns 起点与终点；对象不支持端点协议时返回 null。
     */
    private getCollectionSourceEndpoints(
        source: AbstractSketchEdgeObject,
    ): { startPoint: SketchPointObject; endPoint: SketchPointObject } | null {
        const startGetter = Reflect.get(source, 'getEdgeStartPoint');
        const endGetter = Reflect.get(source, 'getEdgeEndPoint');
        if (typeof startGetter !== 'function' || typeof endGetter !== 'function') {
            console.error('轮廓集合源对象缺少端点访问协议。', { source });
            return null;
        }

        const endpointAccessor = source as unknown as ContourSourceEndpointAccessor;
        return {
            startPoint: endpointAccessor.getEdgeStartPoint(),
            endPoint: endpointAccessor.getEdgeEndPoint(),
        };
    }
}
